import type { AdvisorEntry, CommittedDelta, ThreadEntry } from "@pax/world";
import type { WorldState } from "./world-state";

/** A conversation whose latest player messages the game master has not answered. */
export type WaitingChat =
  | {
      type: "thread";
      key: string;
      perspectiveHandle: string;
      threadId: string;
      /** The player's entries the game master has not answered, oldest first. */
      entries: ThreadEntry[];
      deltas: CommittedDelta[];
    }
  | {
      type: "advisor";
      key: string;
      userId: string;
      /** The user's messages the advisor has not answered, oldest first. */
      entries: AdvisorEntry[];
      deltas: CommittedDelta[];
    };

/**
 * Every chat waiting for the game master, oldest first.
 *
 * A chat waits for each entry a player writes in it until `answeredThrough`, the game master's own
 * record keyed by `WaitingChat["key"]`, reaches that entry's revision. Entries written after that
 * revision keep waiting. A chat the game master has no record of, as when it takes over a game
 * another game master ran, counts as answered through the last time any game master wrote in it.
 *
 * A thread waits only for what players write between jumps: what they write during a jump answers
 * the jump, and the jump replies to it. A thread with nobody in it waits only for a request, since
 * nobody is there to reply. The advisor waits for every message, jump or not.
 */
export function waitingChats(
  record: readonly CommittedDelta[],
  state: WorldState,
  answeredThrough: ReadonlyMap<string, number>,
): WaitingChat[] {
  const waiting = new Map<string, WaitingChat>();
  const lastWritten = new Map<string, number>();
  let jumping = false;

  for (const delta of record) {
    const { operation, actor, target } = delta;
    if (operation.type === "jump.started") jumping = true;
    if (operation.type === "jump.completed") jumping = false;

    if (actor.type === "gameMaster") {
      for (const key of chatKeysOf(delta)) lastWritten.set(key, delta.revision);
      continue;
    }

    if (operation.type === "thread.append" && actor.type === "perspective") {
      const key = threadKey(actor.handle, operation.threadId);
      if (jumping) {
        waiting.delete(key);
        continue;
      }
      const chat = waiting.get(key) ?? {
        type: "thread" as const,
        key,
        perspectiveHandle: actor.handle,
        threadId: operation.threadId,
        entries: [],
        deltas: [],
      };
      if (chat.type === "thread") chat.entries.push(operation.entry);
      chat.deltas.push(delta);
      waiting.set(key, chat);
    } else if (
      operation.type === "advisor.entryAdded" &&
      operation.entry.from === "user" &&
      target.type === "users"
    ) {
      for (const userId of target.userIds) {
        const key = advisorKey(userId);
        const chat = waiting.get(key) ?? {
          type: "advisor" as const,
          key,
          userId,
          entries: [],
          deltas: [],
        };
        if (chat.type === "advisor") chat.entries.push(operation.entry);
        chat.deltas.push(delta);
        waiting.set(key, chat);
      }
    }
  }

  return [...waiting.values()]
    .flatMap(chat => {
      const through = answeredThrough.get(chat.key) ?? lastWritten.get(chat.key) ?? 0;
      const unanswered = chat.deltas.flatMap((delta, index) =>
        delta.revision > through ? [index] : [],
      );
      if (unanswered.length === 0) return [];
      return [
        {
          ...chat,
          deltas: unanswered.flatMap(index => chat.deltas[index] ?? []),
          entries: unanswered.flatMap(index => chat.entries[index] ?? []),
        } as WaitingChat,
      ];
    })
    .filter(chat => {
      if (chat.type === "advisor") return true;
      const thread = state.perspectives[chat.perspectiveHandle]?.threads.find(
        candidate => candidate.id === chat.threadId,
      );
      if (thread?.status !== "open") return false;
      return (
        thread.members.length > 0 || chat.entries.some(entry => entry.content.type === "request")
      );
    })
    .sort((left, right) => (left.deltas[0]?.revision ?? 0) - (right.deltas[0]?.revision ?? 0));
}

/** The chats a game master's delta writes in: a thread of each perspective, or an advisor. */
function chatKeysOf({ operation, target }: CommittedDelta): string[] {
  if (operation.type === "advisor.entryAdded")
    return operation.entry.from === "advisor" && target.type === "users"
      ? target.userIds.map(advisorKey)
      : [];
  if (!operation.type.startsWith("thread.") || target.type !== "perspectives") return [];
  const threadId =
    operation.type === "thread.update"
      ? operation.id
      : "threadId" in operation
        ? operation.threadId
        : undefined;
  if (threadId === undefined) return [];
  return target.perspectives.flatMap(ref =>
    ref.type === "perspective" ? [threadKey(ref.handle, threadId)] : [],
  );
}

/** The key a thread's waiting chat, and any work answering it, goes under. */
function threadKey(perspectiveHandle: string, threadId: string): string {
  return `thread:${perspectiveHandle}:${threadId}`;
}

function advisorKey(userId: string): string {
  return `advisor:${userId}`;
}
